import { useState } from 'react';
import kit from './kit.module.css';
import styles from './SourceAttributionDemo.module.css';

interface Source {
  id: string;
  name: string;
  rating: number;
  reviews: number;
}

const SOURCES: Source[] = [
  { id: 'a', name: 'Review site A', rating: 9.1, reviews: 1200 },
  { id: 'b', name: 'Review site B', rating: 8.7, reviews: 600 },
  { id: 'c', name: 'Review site C', rating: 8.6, reviews: 300 },
];

const HELPER = 'Turn sources off to see how much the pick depends on each one.';

/** Review-weighted rating across the sources that are switched on */
function combinedRating(active: Source[]) {
  const reviews = active.reduce((sum, s) => sum + s.reviews, 0);
  if (!reviews) return { rating: null, reviews: 0 };
  const rating = active.reduce((sum, s) => sum + s.rating * s.reviews, 0) / reviews;
  return { rating: rating.toFixed(1), reviews };
}

export function SourceAttributionDemo() {
  const [enabled, setEnabled] = useState<Record<string, boolean>>({ a: true, b: true, c: true });
  const [showSponsored, setShowSponsored] = useState(false);

  const active = SOURCES.filter((s) => enabled[s.id]);
  const { rating, reviews } = combinedRating(active);
  const single = active.length === 1;

  const toggle = (id: string) => setEnabled((prev) => ({ ...prev, [id]: !prev[id] }));

  let feedback = HELPER;
  if (active.length === 0) feedback = 'With every source off, the pick has nothing behind it - an agent should not recommend it.';
  else if (single) feedback = 'One source left. The agent says so plainly instead of presenting it as a consensus.';
  else if (active.length < SOURCES.length) feedback = 'The rating and review count update with each source you turn off.';

  return (
    <div className={kit.root}>
      <div className={kit.bubbleUser}>Find me a hotel near the conference center in Lisbon, under €180.</div>

      <div className={kit.agent}>
        <span className={kit.agentIcon} aria-hidden="true">✦</span>
        <p>My pick is Casa Alfama. Here's what it's based on.</p>
      </div>

      <div className={kit.split}>
        <div className={kit.proposal}>
          <div className={kit.proposalTop}>
            <span>Recommended</span>
            <span>€164 / night</span>
          </div>
          <h3 className={kit.proposalTitle}>Casa Alfama</h3>
          <div className={styles.ratingRow}>
            <span className={styles.rating}>{rating ?? '-'}</span>
            <span className={styles.ratingMeta}>
              {active.length === 0
                ? 'No sources selected'
                : `Based on ${active.length} ${active.length === 1 ? 'source' : 'sources'} · ${reviews.toLocaleString('en-US')} reviews`}
            </span>
          </div>
          <div className={kit.row}>
            {single && <span className={`${kit.chip} ${kit.chipWarn}`}>Based on a single source</span>}
            {active.length === 0 && <span className={`${kit.chip} ${kit.chipBad}`}>No evidence</span>}
            <span className={kit.chip}>Price checked 10 min ago</span>
          </div>
        </div>

        <div className={styles.sources}>
          <span className={kit.label}>Sources</span>
          <div className={kit.stack}>
            {SOURCES.map((s) => (
              <button
                key={s.id}
                type="button"
                aria-pressed={enabled[s.id]}
                className={`${styles.source} ${enabled[s.id] ? styles.sourceOn : ''}`}
                onClick={() => toggle(s.id)}
              >
                <span className={styles.sourceName}>{s.name}</span>
                <span className={styles.sourceMeta}>
                  {s.rating} · {s.reviews.toLocaleString('en-US')} reviews
                </span>
                <span className={styles.switch} aria-hidden="true" />
              </button>
            ))}
          </div>
          <button
            type="button"
            className={`${kit.btn} ${kit.btnEdit}`}
            aria-pressed={showSponsored}
            onClick={() => setShowSponsored((v) => !v)}
          >
            {showSponsored ? 'Hide sponsored options' : 'Show sponsored options'}
          </button>
        </div>
      </div>

      {showSponsored && (
        <div className={`${kit.proposal} ${styles.sponsored}`}>
          <div className={kit.proposalTop}>
            <span>Passed on</span>
            <span>€149 / night</span>
          </div>
          <h3 className={kit.proposalTitle}>Hotel Baixa Prime</h3>
          <div className={kit.row}>
            <span className={`${kit.chip} ${kit.chipWarn}`}>Sponsored</span>
            <span className={styles.ratingSmall}>9.4</span>
            <span className={kit.chip}>1 source</span>
          </div>
          <p className={kit.feedback}>Cheaper, but the listing is paid and the rating comes from one site only.</p>
        </div>
      )}

      <p className={kit.feedback} aria-live="polite">{feedback}</p>
    </div>
  );
}
